'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { Configure, Index, useHits } from 'react-instantsearch-hooks-web';
import CustomStats from 'components/commercetools-ui/atoms/search-algolia/customstats';
import InstantSearch from 'components/HOC/InstantSearch';
import { SuggestionsFetcher } from 'components/commercetools-ui/organisms/no-result';
import ProductList from 'components/commercetools-ui/organisms/product/product-list-algolia';
import ProductListProvider, {
  useProductList,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/context';
import {
  FacetConfiguration,
  PriceConfiguration,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/types';
import { facetsByAttribute, parseFacetConfigs, pricesByAttribute } from 'helpers/algolia/listing-facets';
import { productsQuerySuggestionsIndex_AU, productsQuerySuggestionsIndex_NZ } from 'helpers/constants/algolia';
import useContentSearchTerm from 'helpers/hooks/useContentSearchTerm';
import { getLocalizationInfo } from 'project.config';
import LocalizedIndex from 'providers/algolia/localized-index';
import { TasticProps } from 'frontastic/tastics/types';
import ContentListAlgolia from './content-list';

/** Frontastic Studio configuration of the /search tastic. */
export interface Props {
  facetsConfiguration: FacetConfiguration[];
  pricesConfiguration: PriceConfiguration[];
}

function ProductListTastic({
  noContentSuggestions,
  setProductResults,
  data,
  suggestions,
}: TasticProps<Props>) {
  const { updateFacetsConfiguration, updatePricesConfiguration } = useProductList();
  const { locale, slug } = useParams();
  const { results } = useHits();
  const { currency } = getLocalizationInfo(locale);

  // `slug` from useParams is a string[] on the catch-all route; the last segment names the page.
  const categorySlug = Array.isArray(slug) ? slug.at(-1) : (slug as string | undefined)?.split('/').at(-1);

  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('query') as string;

  // Studio keys stay raw (`searchableAttributes.*`): on this path they go straight to Algolia.
  const facetConfigs = useMemo(() => parseFacetConfigs(data.facetsConfiguration), [data.facetsConfiguration]);
  const facetsConfiguration = useMemo(
    () => facetsByAttribute(facetConfigs, { currency, canonical: false }),
    [facetConfigs, currency],
  );
  const pricesConfiguration = useMemo(
    () => pricesByAttribute(facetConfigs, { currency, canonical: false }, data.pricesConfiguration ?? []),
    [facetConfigs, currency, data.pricesConfiguration],
  );

  useEffect(() => {
    updateFacetsConfiguration(facetsConfiguration);
    updatePricesConfiguration(pricesConfiguration);
  }, [facetsConfiguration, pricesConfiguration, updateFacetsConfiguration, updatePricesConfiguration]);

  useEffect(() => {
    setProductResults(results);
  }, [results]);

  return (
    <ProductList
      slug={categorySlug}
      searchQuery={searchQuery}
      facetsConfiguration={facetsConfiguration}
      noContentSuggestions={noContentSuggestions}
      suggestions={suggestions}
    />
  );
}

/**
 * `commercetools/ui/products/product-list-algolia` — the /search results page (BROWSER PATH).
 *
 * The only product listing that queries Algolia from the browser: InstantSearch against the search
 * indices (caroma-au-main / caroma-nz-main, see providers/algolia/localized-index) through
 * algolia/cachedProductsSearchClient. Every other listing is a V2 CMS block on the server path
 * (cms-components-v2/section-with-product-listing) and shares nothing with this tastic but the tile.
 */
function ProductListAlgoliaTastic({ data, ...props }: TasticProps<Props>) {
  // `undefined` until the content query resolves, so a real 0 ("no content for this term") is
  // distinguishable from "not known yet" — the content-term fallback keys off exactly that.
  const [noContentSuggestions, setNoContentSuggestion] = useState<number | undefined>(undefined);
  const { locale } = useParams();
  const searchParams = useSearchParams();

  const searchQuery = searchParams.get('query') as string;
  // `undefined` = the products query has not resolved yet, so the content term is still unknown.
  // '' = resolved with no product-derived term (the raw search term should be used).
  const [productQuery, setProductQuery] = useState<string | undefined>(undefined);
  const [productsResults, setProductResults] = useState<any>();
  const [suggestions, setSuggestions] = useState<any>([]);

  // Content suggestions search the visitor's own term first, and only fall back to the product-derived
  // term (collection/category of the matched product) if that finds no content at all.
  const contentSearchTerm = useContentSearchTerm({
    searchTerm: searchQuery,
    derivedTerm: productQuery,
    contentHits: noContentSuggestions,
  });

  // A search that found nothing: fetch "Did you mean?" query suggestions. (This compared `slug` to
  // 'search', but on the catch-all route `slug` is ['search'], so the suggestions never loaded.)
  const isSearchWithNoProducts = !!searchQuery && !productsResults?.__isArtificial && productsResults?.nbHits === 0;
  const indexes = {
    products: {
      au: productsQuerySuggestionsIndex_AU,
      nz: productsQuerySuggestionsIndex_NZ,
    },
  };

  return (
    <InstantSearch cacheProducts>
      {isSearchWithNoProducts && (<Index indexName={indexes['products'][locale as 'au' | 'nz']}>
        <Configure hitsPerPage={5} />
        <SuggestionsFetcher setSuggestions={setSuggestions} query={searchQuery} />
      </Index>)}
      <LocalizedIndex type="products">
        <ProductListProvider>
          <CustomStats setProductQuery={setProductQuery} searchQuery={searchQuery} />
          <ProductListTastic
            noContentSuggestions={noContentSuggestions}
            setProductResults={setProductResults}
            data={{ ...data }}
            suggestions={suggestions}
            {...props}
          />
        </ProductListProvider>
      </LocalizedIndex>
      {/*
        Mounted straight away: the first content query uses the visitor's own term, which needs nothing
        from the products query. It only re-queries with the product-derived term when the raw term came
        back empty — so the common case (content exists for what was typed) costs exactly one query.
      */}
      {searchQuery && (
        <LocalizedIndex type="content">
          <ContentListAlgolia
            searchQuery={searchQuery}
            contentQuery={contentSearchTerm}
            setNoContentSuggestion={setNoContentSuggestion}
            productResults={productsResults}
          />
        </LocalizedIndex>
      )}
    </InstantSearch>
  );
}

export default ProductListAlgoliaTastic;
